export type BootVisualProfile = "low" | "high";

export type BootVisualProfileDecision = {
  profile: BootVisualProfile;
  reason: string;
};

/** The Network Information API (Chromium; `moz` / `webkit` prefixed in older engines): not in the DOM lib. */
type NetworkHints = { readonly effectiveType?: string; readonly saveData?: boolean };

/** `navigator` with what the profile reads beyond the DOM lib, each possibly absent (`deviceMemory` is Chromium-only too). */
type ProfiledNavigator = Navigator & {
  readonly connection?: NetworkHints;
  readonly mozConnection?: NetworkHints;
  readonly webkitConnection?: NetworkHints;
  readonly deviceMemory?: number;
};

export function resolveBootVisualProfile(input?: {
  forcedProfile?: BootVisualProfile | null;
}): BootVisualProfileDecision {
  const forced = input?.forcedProfile ?? null;
  if (forced) {
    return {
      profile: forced,
      reason: `forced:${forced}`,
    };
  }

  const nav = typeof navigator !== "undefined" ? (navigator as ProfiledNavigator) : null;
  const connection = nav?.connection ?? nav?.mozConnection ?? nav?.webkitConnection ?? null;

  const effectiveType = String(connection?.effectiveType ?? "");
  const saveData = !!connection?.saveData;
  const hardwareConcurrency = Number(nav?.hardwareConcurrency ?? 8);
  const deviceMemory = Number(nav?.deviceMemory ?? 8);

  const weakNetwork =
    effectiveType === "slow-2g" ||
    effectiveType === "2g" ||
    effectiveType === "3g" ||
    saveData;

  const weakDevice =
    hardwareConcurrency > 0 && hardwareConcurrency <= 4 ||
    deviceMemory > 0 && deviceMemory <= 4;

  if (weakNetwork || weakDevice) {
    return {
      profile: "low",
      reason: `auto: network=${effectiveType || "unknown"} saveData=${saveData} cpu=${hardwareConcurrency} mem=${deviceMemory}`,
    };
  }

  return {
    profile: "high",
    reason: `auto: network=${effectiveType || "unknown"} saveData=${saveData} cpu=${hardwareConcurrency} mem=${deviceMemory}`,
  };
}